import { Array, Boolean, Literal, Null, Number, Optional, Record, Static, String, Undefined, Union } from 'runtypes';

import { IdoButtonRunType } from 'src/models/ido';
import { PhoneCallExtrasRunType } from 'src/models/phoneCall/extras';
import { SpoingButtonRunType } from 'src/models/spoing';
import { StatusButtonRunType } from 'src/models/status';
import { TaskButtonRunType } from 'src/models/task';

const CallAvailableCallListRunType = Record({
	callListName: String,
	id: String,
});
export const CallAvailableCallerNameRunType = Record({
	displayName: String,
	id: String,
});

export type CallAvailableCallerName = Static<typeof CallAvailableCallerNameRunType>;

export const CallAvailableUserRunType = Record({
	displayName: String,
	fullName: String,
	id: Number,
	userAvatar: String,
});

export type CallAvailableUser = Static<typeof CallAvailableUserRunType>;

const CallAllUsersRunType = Record({
	displayName: String,
	fullName: String,
	id: Number,
	userAvatar: String,
});

export const PhoneCallRunType = Record({
	aggregateId: String,
	aggregateVersion: Number,
	callAllUsers: Array(CallAllUsersRunType),
	callAvailableCallLists: Array(CallAvailableCallListRunType),
	callAvailableCallerNames: Array(CallAvailableCallerNameRunType),
	callAvailableUsers: Array(CallAvailableUserRunType),
	callDate: String,
	callList: String,
	callListId: String,
	callReason: String,
	callReceivedBy: String.Or(Number),
	callReceivedFor: String.Or(Number),
	callSummary: String,
	callTime: String,
	callTimestamp: Number,
	callbackDesired: Boolean,
	calledNumber: String,
	callerName: String.Or(Null),
	callerNumber: String,
	contactCallList: String,
	contactPicture: Null.Or(String).Or(Undefined),
	contactPictureVersion: Number.Or(String),
	direction: String,
	duration: Number,
	extras: PhoneCallExtrasRunType,
	idoButton: IdoButtonRunType,
	newCallerName: String,
	spoingButton: SpoingButtonRunType,
	statusButton: StatusButtonRunType,
	taskButton: TaskButtonRunType,
	updatedCallerName: String,
});

export type PhoneCall = Static<typeof PhoneCallRunType>;

export const OutgoingCallConfigurationForTheUserRunType = Record({
	adminPassword: Optional(String),
	adminUser: Optional(String),
	call_polling: Optional(Literal('on')),
	domain: Optional(String),
	error: Optional(
		Literal('USER_IS_NOT_ALLOWED_TO_INITIATE_OUTGOING_PHONECALLS')
			.Or(Literal('USER_HAS_NO_TERMINAL_CONFIGURED'))
			.Or(Literal('USER_HAS_UNSUPPORTED_LOCATION_CONFIGURED'))
			.Or(Literal('USER_HAS_NO_LOCATION_CONFIGURED'))
			.Or(Literal('USER_HAS_NO_CONFIGURATION'))
			.Or(Literal('LOCATION_HAS_NO_CONFIGURATION'))
	),
	location: Literal('asterisk')
		.Or(Literal('tapimasterExtern'))
		.Or(Literal('curlPost'))
		.Or(Literal('sipgateIO'))
		.Or(Literal('starface')),
	moduleName: Optional(String),
	terminal: String,
	token: Optional(String),
	tokenId: Optional(String),
	url: Optional(String),
	url_hangup: Optional(String),
	url_init: Optional(String),
});

export type OutgoingCallConfigurationForTheUser = Static<typeof OutgoingCallConfigurationForTheUserRunType>;

export const NextOutgoingCallIdForACalledNumberAfterAGivenTimestampRunType = Union(
	Record({
		nextOutgoingCallId: String,
	}),
	Record({
		error: Literal('NO_NEXT_OUTGOING_CALL_FOUND'),
	})
);

export type NextOutgoingCallIdForACalledNumberAfterAGivenTimestamp = Static<
	typeof NextOutgoingCallIdForACalledNumberAfterAGivenTimestampRunType
>;

const PhoneticSimilarPhoneBookEntryRunType = Record({
	id: Number,
	linkCount: Number,
	name: String,
	number: String,
});

export const PhoneticSimilarPhoneBookEntriesRunType = Record({
	countOfPhoneticSimilarEntries: Number,
	duplicateIdTuple: String,
	phoneticSimilarEntries: Array(PhoneticSimilarPhoneBookEntryRunType),
});

export type PhoneticSimilarPhoneBookEntries = Static<typeof PhoneticSimilarPhoneBookEntriesRunType>;

const PhoneBookEntryRunType = Record({
	id: Number,
	name: String,
	number: String,
});

export const PhoneBookEntriesRunType = Record({
	completeCount: Number,
	phoneBookEntries: Array(PhoneBookEntryRunType),
});

export type PhoneBookEntries = Static<typeof PhoneBookEntriesRunType>;
